import { useState, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import { useTree } from '@/hooks/useTree';
import { useTreeStore } from '@/store/useTreeStore';
import { PersonAutocomplete } from '@/components/PersonForm/PersonAutocomplete';
import { AppLayout } from '@/components/Layout/AppLayout';
import type { TreeNode } from '@/api/tree.api';

// ─── Konstanten ──────────────────────────────────────────────────────────────

const CENTER_R = 42;       // Radius des Root-Kreises
const RING_W   = 88;       // Breite jedes Generationsrings
const PAD      = 24;       // SVG-Innenabstand

// ─── Farben ──────────────────────────────────────────────────────────────────

const COLORS: Record<string, { fill: string; text: string }> = {
  male:    { fill: '#bfdbfe', text: '#1e3a5f' }, // blue-200 / blue-900
  female:  { fill: '#fbcfe8', text: '#6b1040' }, // pink-200 / pink-900
  other:   { fill: '#d9f99d', text: '#365314' }, // lime-200 / lime-900
  unknown: { fill: '#e2e8f0', text: '#475569' }, // slate-200 / slate-600
};
const EMPTY_FILL   = '#f8fafc'; // slate-50
const EMPTY_STROKE = '#e2e8f0'; // slate-200

// ─── Typen ────────────────────────────────────────────────────────────────────

interface AncestorSlot {
  ahnNr: number;   // Ahnentafel-Nummer (1 = Startperson, 2 = Vater, 3 = Mutter, …)
  gen: number;     // Generation (0 = Startperson)
  slot: number;    // Position innerhalb der Generation (0-basiert)
  node: TreeNode | null;
}

// ─── Vorfahren-Karte aufbauen ─────────────────────────────────────────────────

function buildAncestors(
  nodes: TreeNode[],
  rootId: string,
  maxGen: number,
): Map<number, TreeNode> {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const result = new Map<number, TreeNode>();

  const queue: Array<{ n: number; id: string }> = [{ n: 1, id: rootId }];
  const root = byId.get(rootId);
  if (root) result.set(1, root);

  while (queue.length > 0) {
    const { n, id } = queue.shift()!;
    const gen = Math.floor(Math.log2(n));
    if (gen >= maxGen) continue;

    const node = byId.get(id);
    if (!node) continue;

    // Ahnentafel: Vater = 2n, Mutter = 2n+1
    if (node.primaryParentId) {
      const p = byId.get(node.primaryParentId);
      if (p) { result.set(n * 2, p); queue.push({ n: n * 2, id: node.primaryParentId }); }
    }
    if (node.secondaryParentId) {
      const p = byId.get(node.secondaryParentId);
      if (p) { result.set(n * 2 + 1, p); queue.push({ n: n * 2 + 1, id: node.secondaryParentId }); }
    }
  }

  return result;
}

// ─── Alle Slots für maxGen Generationen ──────────────────────────────────────

function buildSlots(
  ancestors: Map<number, TreeNode>,
  maxGen: number,
): AncestorSlot[] {
  const slots: AncestorSlot[] = [];
  for (let gen = 0; gen <= maxGen; gen++) {
    const count = Math.pow(2, gen);
    for (let slot = 0; slot < count; slot++) {
      const ahnNr = count + slot;
      slots.push({ ahnNr, gen, slot, node: ancestors.get(ahnNr) ?? null });
    }
  }
  return slots;
}

// ─── SVG-Arc-Generator ───────────────────────────────────────────────────────

const arcGen = d3
  .arc<{ startAngle: number; endAngle: number; innerRadius: number; outerRadius: number }>()
  .startAngle((d) => d.startAngle)
  .endAngle((d) => d.endAngle)
  .innerRadius((d) => d.innerRadius)
  .outerRadius((d) => d.outerRadius)
  .padAngle(0.012)
  .cornerRadius(2);

// Winkel aus Slot-Position berechnen (Halbkreis: –π/2 … +π/2)
function slotAngles(gen: number, slot: number) {
  const slotsInGen = Math.pow(2, gen);
  const w = Math.PI / slotsInGen;
  const start = -Math.PI / 2 + slot * w;
  return { startAngle: start, endAngle: start + w, midAngle: start + w / 2 };
}

// Kartesische Koordinaten für D3-Winkel (0 = oben, im Uhrzeigersinn)
function polarToCart(r: number, angle: number) {
  return { x: r * Math.sin(angle), y: -r * Math.cos(angle) };
}

// ─── Einzelnes Segment ────────────────────────────────────────────────────────

interface SegmentProps {
  s: AncestorSlot;
  onClick: (node: TreeNode) => void;
}

function Segment({ s, onClick }: SegmentProps) {
  const { gen, slot, node } = s;

  if (gen === 0) return null; // Root wird separat gerendert

  const innerR = CENTER_R + (gen - 1) * RING_W;
  const outerR = CENTER_R + gen * RING_W;
  const midR   = (innerR + outerR) / 2;
  const { startAngle, endAngle, midAngle } = slotAngles(gen, slot);

  const d = arcGen({ startAngle, endAngle, innerRadius: innerR, outerRadius: outerR }) ?? '';
  const { x: cx, y: cy } = polarToCart(midR, midAngle);

  // Textrotation: Text zeigt radial nach außen, bleibt lesbar
  const midDeg  = (midAngle * 180) / Math.PI;
  const rotDeg  = midDeg; // Im Bereich –90°…+90° ist Text immer lesbar

  const color   = node ? (COLORS[node.gender ?? 'unknown'] ?? COLORS.unknown) : null;
  const slotW   = Math.PI / Math.pow(2, gen); // Winkelbreite in rad

  // Welche Textzeillen zeigen?
  const showName = gen <= 3 && slotW >= 0.28;
  const showYear = gen <= 2 && slotW >= 0.6;

  const firstName = node?.firstName ?? '';
  const lastName  = node?.lastName  ?? '';
  const name = gen === 1
    ? `${firstName} ${lastName}`.trim()
    : gen === 2
      ? `${firstName} ${lastName}`.trim()
      : lastName || firstName;

  const birthYear = node?.birthDateText
    ? node.birthDateText.match(/\d{4}/)?.[0] ?? ''
    : '';

  const clipId = `clip-${s.ahnNr}`;

  return (
    <g style={node ? { cursor: 'pointer' } : undefined} onClick={() => node && onClick(node)}>
      <defs>
        <clipPath id={clipId}>
          <path d={d} />
        </clipPath>
      </defs>

      {/* Segment-Fläche */}
      <path
        d={d}
        fill={color ? color.fill : EMPTY_FILL}
        stroke={color ? '#fff' : EMPTY_STROKE}
        strokeWidth={1.5}
        className={node ? 'transition-opacity hover:opacity-75' : ''}
      >
        {node && (
          <title>
            {`${firstName} ${lastName}`.trim()}
            {birthYear ? ` (* ${birthYear})` : ''}
          </title>
        )}
      </path>

      {/* Text */}
      {showName && node && (
        <g clipPath={`url(#${clipId})`}>
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="middle"
            transform={`rotate(${rotDeg}, ${cx}, ${cy})`}
            fill={color!.text}
            fontSize={gen === 1 ? 11 : gen === 2 ? 9.5 : 8.5}
            fontWeight={gen <= 2 ? 500 : 400}
            pointerEvents="none"
          >
            {showYear && birthYear ? (
              <>
                <tspan x={cx} dy="-0.6em">{name}</tspan>
                <tspan x={cx} dy="1.3em" fontSize={gen === 1 ? 9 : 8} opacity={0.75}>{birthYear}</tspan>
              </>
            ) : (
              name
            )}
          </text>
        </g>
      )}
    </g>
  );
}

// ─── Legende ─────────────────────────────────────────────────────────────────

function Legend({ slots }: { slots: AncestorSlot[] }) {
  const filled  = slots.filter((s) => s.gen > 0 && s.node).length;
  const total   = slots.filter((s) => s.gen > 0).length;
  const missing = total - filled;

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-500">
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-sm" style={{ background: COLORS.male.fill, border: '1px solid #93c5fd' }} />
        Männlich
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-sm" style={{ background: COLORS.female.fill, border: '1px solid #f9a8d4' }} />
        Weiblich
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-3 w-3 rounded-sm" style={{ background: COLORS.unknown.fill, border: '1px solid #cbd5e1' }} />
        Unbekannt
      </span>
      <span className="text-slate-400">|</span>
      <span>{filled} von {total} Vorfahren bekannt</span>
      {missing > 0 && <span className="text-slate-400">({missing} fehlen)</span>}
    </div>
  );
}

// ─── FanChartPage ─────────────────────────────────────────────────────────────

export function FanChartPage() {
  const { data: treeData, isLoading } = useTree();
  const setSelectedPerson = useTreeStore((s) => s.setSelectedPerson);

  const [rootId, setRootId] = useState<string | null>(null);
  const [maxGen, setMaxGen] = useState(4);

  const ancestors = useMemo(() => {
    if (!treeData || !rootId) return new Map<number, TreeNode>();
    return buildAncestors(treeData.nodes, rootId, maxGen);
  }, [treeData, rootId, maxGen]);

  const slots = useMemo(
    () => buildSlots(ancestors, maxGen),
    [ancestors, maxGen],
  );

  const handleNodeClick = useCallback(
    (node: TreeNode) => setSelectedPerson(node.id),
    [setSelectedPerson],
  );

  // SVG-Dimensionen
  const maxR   = CENTER_R + maxGen * RING_W;
  const svgW   = maxR * 2 + PAD * 2;
  const svgH   = maxR + PAD + CENTER_R + 10;
  const originX = svgW / 2;
  const originY = svgH - PAD;

  const rootNode = rootId ? ancestors.get(1) : null;
  const rootColor = rootNode
    ? (COLORS[rootNode.gender ?? 'unknown'] ?? COLORS.unknown)
    : COLORS.unknown;

  return (
    <AppLayout>
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="border-b border-slate-200 bg-white px-6 py-4">
          <h1 className="text-xl font-semibold text-slate-900">Fächerdiagramm</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Vorfahren einer Person bis zu {maxGen} Generationen
          </p>
        </div>

        {/* Einstellungen */}
        <div className="border-b border-slate-100 bg-white px-6 py-3">
          <div className="flex flex-wrap items-end gap-4">
            {/* Personenauswahl */}
            <div className="min-w-64 flex-1">
              <label className="mb-1 block text-xs font-medium text-slate-500">
                Startperson
              </label>
              {isLoading ? (
                <div className="h-9 animate-pulse rounded border border-slate-200 bg-slate-100" />
              ) : (
                <PersonAutocomplete
                  value={rootId}
                  onChange={setRootId}
                  placeholder="Person suchen…"
                />
              )}
            </div>

            {/* Generationen-Slider */}
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">
                Generationen: <span className="font-semibold text-slate-700">{maxGen}</span>
              </label>
              <input
                type="range"
                min={2}
                max={6}
                value={maxGen}
                onChange={(e) => setMaxGen(Number(e.target.value))}
                className="h-2 w-36 cursor-pointer accent-blue-600"
              />
            </div>
          </div>
        </div>

        {/* Chart-Bereich */}
        <div className="flex-1 overflow-auto bg-slate-50 p-4">
          {!rootId ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-slate-400">
              <svg className="h-12 w-12 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
              </svg>
              <p className="text-sm">Wähle eine Startperson aus, um das Fächerdiagramm anzuzeigen.</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              {/* SVG Fan Chart */}
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
                <svg
                  width={svgW}
                  height={svgH}
                  viewBox={`0 0 ${svgW} ${svgH}`}
                  style={{ maxWidth: '100%', height: 'auto' }}
                >
                  <g transform={`translate(${originX}, ${originY})`}>
                    {/* Generationsringe (Hintergrundlinien) */}
                    {Array.from({ length: maxGen + 1 }, (_, g) => {
                      const r = CENTER_R + g * RING_W;
                      // Halbkreis-Bogen als Orientierungslinie
                      const x1 = -r, x2 = r;
                      return (
                        <path
                          key={g}
                          d={`M ${x1} 0 A ${r} ${r} 0 0 1 ${x2} 0`}
                          fill="none"
                          stroke="#e2e8f0"
                          strokeWidth={0.5}
                        />
                      );
                    })}

                    {/* Generationsring-Beschriftungen (rechts außen) */}
                    {Array.from({ length: maxGen }, (_, g) => {
                      const gen = g + 1;
                      const midR = CENTER_R + (gen - 0.5) * RING_W;
                      const labels = ['Eltern', 'Groß­eltern', 'Urgroß­eltern', 'Ururgroß­eltern', 'Urururgroß­eltern'];
                      return (
                        <text
                          key={gen}
                          x={midR * Math.sin(Math.PI / 2) + 4}
                          y={-midR * Math.cos(Math.PI / 2)}
                          fontSize={8}
                          fill="#94a3b8"
                          dominantBaseline="middle"
                        >
                          {labels[gen - 1] ?? `Gen. ${gen}`}
                        </text>
                      );
                    })}

                    {/* Alle Segmente rendern */}
                    {slots.map((s) => (
                      <Segment key={s.ahnNr} s={s} onClick={handleNodeClick} />
                    ))}

                    {/* Root-Kreis */}
                    <circle
                      r={CENTER_R - 1}
                      fill={rootColor.fill}
                      stroke="#fff"
                      strokeWidth={2}
                      style={{ cursor: 'pointer' }}
                      onClick={() => rootNode && setSelectedPerson(rootNode.id)}
                    />
                    {rootNode && (
                      <>
                        <text
                          x={0}
                          y={rootNode.birthDateText ? -5 : 0}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize={10}
                          fontWeight={600}
                          fill={rootColor.text}
                          pointerEvents="none"
                        >
                          {rootNode.firstName}
                        </text>
                        {rootNode.birthDateText && (
                          <text
                            x={0}
                            y={8}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={8}
                            fill={rootColor.text}
                            opacity={0.7}
                            pointerEvents="none"
                          >
                            {rootNode.birthDateText.match(/\d{4}/)?.[0] ?? rootNode.birthDateText}
                          </text>
                        )}
                      </>
                    )}
                  </g>
                </svg>
              </div>

              {/* Legende */}
              <Legend slots={slots} />
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
